import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { type ReactElement, type ReactNode, useState } from 'react'

import { ErrorState } from '@/components/state/error-state'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * Tabela do painel, sobre o TanStack Table.
 *
 * Os três estados obrigatórios da DoD vivem AQUI, e não em cada tela: enquanto
 * carrega, esqueletos com a mesma altura da linha (a tabela não "pula" quando
 * o dado chega); em erro, a mensagem em pt-BR com "tentar de novo"; vazio, o
 * conteúdo que a tela definir. Nenhum caminho leva a uma área branca.
 *
 * A ordenação é do CLIENTE e vale para a página carregada. Filtro e paginação
 * são do SERVIDOR — ordenar 20 linhas no navegador é instantâneo; filtrar
 * clientes carregando a base inteira não é uma opção.
 *
 * Coluna de NÚMERO (dinheiro, contagem, fator) declara
 * `meta: { alinhamento: 'direita' }`: o cabeçalho e a célula inteira vão para a
 * direita juntos. Alinhar só o valor deixava o título da coluna na outra borda
 * e a linha de baixo da célula (o "a partir de…", o "maior vence") à esquerda —
 * o desalinhamento que o QA de 30/10 apontou nos cupons.
 */

declare module '@tanstack/react-table' {
  // A assinatura tem de ser idêntica à do TanStack para a declaração fundir.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    readonly alinhamento?: 'direita'
  }
}

export interface TabelaDeDadosProps<TDado> {
  readonly colunas: readonly ColumnDef<TDado>[]
  readonly dados: readonly TDado[]
  readonly carregando: boolean
  readonly erro?: unknown
  readonly aoTentarNovamente?: () => void
  /** O que mostrar quando não há nenhuma linha. */
  readonly estadoVazio: ReactNode
  /** Descrição da tabela para leitor de tela. */
  readonly legenda: string
  readonly ordenacaoInicial?: SortingState
}

export function TabelaDeDados<TDado>({
  colunas,
  dados,
  carregando,
  erro,
  aoTentarNovamente,
  estadoVazio,
  legenda,
  ordenacaoInicial = [],
}: TabelaDeDadosProps<TDado>): ReactElement {
  const [ordenacao, setOrdenacao] = useState<SortingState>(ordenacaoInicial)

  const tabela = useReactTable({
    // O TanStack Table pede arrays mutáveis; as props são `readonly` porque
    // nada aqui altera a lista. A conversão é só de variância.
    data: dados as TDado[],
    columns: colunas as ColumnDef<TDado>[],
    state: { sorting: ordenacao },
    onSortingChange: setOrdenacao,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (erro !== undefined && erro !== null) {
    return (
      <ErrorState
        titulo="Não foi possível carregar a lista"
        erro={erro}
        {...(aoTentarNovamente === undefined ? {} : { aoTentarNovamente })}
      />
    )
  }

  if (carregando) {
    const linhas = [0, 1, 2, 3, 4]

    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="space-y-2 rounded-lg border border-border bg-surface p-4"
      >
        <span className="sr-only">Carregando a lista…</span>
        {linhas.map((linha) => (
          <Skeleton key={linha} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (dados.length === 0) {
    return <>{estadoVazio}</>
  }

  return (
    <Table>
      <TableCaption className="sr-only">{legenda}</TableCaption>

      <TableHeader>
        {tabela.getHeaderGroups().map((grupo) => (
          <TableRow key={grupo.id}>
            {grupo.headers.map((cabecalho) => {
              const ordenavel = cabecalho.column.getCanSort()
              const direcao = cabecalho.column.getIsSorted()
              const conteudo = flexRender(cabecalho.column.columnDef.header, cabecalho.getContext())

              return (
                <TableHead
                  key={cabecalho.id}
                  className={
                    cabecalho.column.columnDef.meta?.alinhamento === 'direita'
                      ? 'text-right'
                      : undefined
                  }
                  aria-sort={
                    direcao === 'asc'
                      ? 'ascending'
                      : direcao === 'desc'
                        ? 'descending'
                        : ordenavel
                          ? 'none'
                          : undefined
                  }
                >
                  {ordenavel ? (
                    <button
                      type="button"
                      onClick={cabecalho.column.getToggleSortingHandler()}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md py-1 uppercase',
                        'transition-colors hover:text-foreground focus-visible:outline-none',
                        'focus-visible:ring-2 focus-visible:ring-ring',
                      )}
                    >
                      {conteudo}
                      {direcao === 'asc' ? (
                        <ArrowUp className="size-3" aria-hidden="true" />
                      ) : direcao === 'desc' ? (
                        <ArrowDown className="size-3" aria-hidden="true" />
                      ) : (
                        <ArrowUpDown className="size-3 opacity-50" aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    conteudo
                  )}
                </TableHead>
              )
            })}
          </TableRow>
        ))}
      </TableHeader>

      <TableBody>
        {tabela.getRowModel().rows.map((linha) => (
          <TableRow key={linha.id}>
            {linha.getVisibleCells().map((celula) => (
              <TableCell
                key={celula.id}
                className={
                  celula.column.columnDef.meta?.alinhamento === 'direita' ? 'text-right' : undefined
                }
              >
                {flexRender(celula.column.columnDef.cell, celula.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
